import { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TokenContext } from "../components/TokenContext";
import { callProxy } from "../api/api";
import {
  Wifi, Car, Coffee, Waves, Dumbbell, Check, BedDouble, MapPin,
  ChevronRight, Star, Heart, Bath, Utensils, Plane, CheckCircle, Building2,
} from "lucide-react";
import { safeString } from "../utils/langUtils";

const AMENITY_ICONS = {
  "free wifi": Wifi, "private parking": Car, "good breakfast": Coffee,
  "swimming pool": Waves, "swimming pools": Waves, "private bathroom": Bath,
  "balcony": Building2, "restaurants": Utensils, "airport shuttle": Plane,
  "ocean view": Waves,
};

export default function ApartmentCard({ apartment, onLike }) {
  const navigate = useNavigate();
  const { token } = useContext(TokenContext);
  const visitorId = localStorage.getItem("visitorId");
  const liked = apartment.likedBy?.includes(visitorId);

  const {
    slug, name, city, country, rating, smoobuId, images,
    reviews, bedrooms, description, highlights = [], amenities = []
  } = apartment;

  const [rateData, setRateData] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);

  const today = new Date().toISOString().split("T")[0];

useEffect(() => {
  if (!smoobuId) return;

  const fetchRates = async () => {
    try {
      setRateLoading(true);

      const res = await callProxy(`/api/rates/${smoobuId}`, token);

      // safer extraction
      const data =
        res?.data?.data?.[String(smoobuId)] ||
        res?.data?.[String(smoobuId)] ||
        res?.data ||
        null;

      setRateData(data);
    } catch (err) {
      console.error("Failed to fetch rates", err);
      setRateData(null);
    } finally {
      setRateLoading(false);
    }
  };

  fetchRates();
}, [smoobuId, token]);


const price = useMemo(() => {
  if (rateLoading) return null; // ⛔ don't render yet

  if (rateData && typeof rateData === "object") {
    const todayPrice = rateData[today]?.price;

    if (typeof todayPrice === "number") {
      return todayPrice;
    }

    // fallback to first available price
    const firstAvailable = Object.values(rateData).find(
      (d) => typeof d?.price === "number"
    )?.price;

    if (firstAvailable) return firstAvailable;
  }

  // only fallback AFTER API finished
  return apartment.price_per_night || null;
}, [rateData, rateLoading, apartment.price_per_night, today]);


  const imageUrl = useMemo(() => {
    if (apartment.featuredImages?.length > 0) return apartment.featuredImages[0];
    if (apartment.images?.length > 0) {
      const first = apartment.images[0];
      return typeof first === "string" ? first : first?.url;
    }
    return apartment.image || "/assets/images/placeholder.jpg";
  }, [apartment]);

  const handleLike = (e) => {
    e.stopPropagation();
    onLike(slug);
  };

  return (
    <article
      className="bg-white border rounded-2xl shadow-sm hover:shadow-md transition cursor-pointer flex flex-col sm:flex-row overflow-hidden"
      onClick={() => navigate(`/apartments/${slug}`)}
    >
      <div className="relative w-full sm:w-56 h-44 sm:h-auto bg-gray-100 flex-shrink-0">
        <img src={imageUrl} alt={name} className="w-full h-full object-cover" loading="lazy" />
        <button onClick={handleLike} className="absolute top-2 right-2 flex items-center gap-1.5 bg-white/95 backdrop-blur-md px-2 py-1 rounded-full shadow">
          <Heart size={14} className={liked ? "text-red-500 fill-red-500" : "text-gray-700"} />
          {apartment.likesCount > 0 && <span className="text-[11px] font-semibold text-gray-800">{apartment.likesCount}</span>}
        </button>
      </div>

      <div className="flex-1 p-4 flex flex-col gap-3 relative">
        {rating && (
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1">
            <Star size={14} className="text-yellow-400 fill-yellow-400" />
            <span className="text-sm font-semibold text-gray-800">{rating}</span>
            {reviews && <span className="text-xs text-gray-500">({reviews})</span>}
          </div>
        )}

        <div>
          <h3 className="text-lg font-semibold text-[#3a2a00] leading-snug">{safeString(name)}</h3>
          <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
            <MapPin size={14} /> {safeString(city)}, {safeString(country)}
          </p>
        </div>

        {description && (
          <p className="text-sm text-gray-600 line-clamp-2">
            {description.replace(/\s+/g, " ").trim()}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {highlights.slice(0, 3).map((item, i) => (
            <span key={i} className="text-xs px-3 py-1 rounded-full bg-green-50 text-green-700 font-medium">{item}</span>
          ))}
        </div>

        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-green-700 font-semibold text-xs">
              <Check size={14} /> Free cancellation
            </div>
            <span className="text-xs font-semibold text-red-600">Only a few rooms left</span>
          </div>

          <div className="text-right">
            <p className="text-xs text-gray-500">Per night</p>
           <p className="text-xl font-bold text-[#3a2a00]">
            {rateLoading ? (
              <span className="text-gray-400 text-sm">Loading...</span>
            ) : price ? (
              <>AED {price.toLocaleString()}</>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </p>

            <button className="bg-[#3a2a00] text-white px-4 py-2 mt-2 rounded-lg flex items-center gap-1 text-sm">
              See availability <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}